import {CONTACT_EMAIL, LegalDocument, OPERATOR_NAME} from './types';

/**
 * Пользовательское соглашение — правила пользования сервисом.
 *
 * Текст подготовлен разработкой и требует юридической проверки до публикации
 * в прод (requirements.md §7).
 */
export const USER_AGREEMENT: LegalDocument = {
    type: 'USER_AGREEMENT',
    title: 'Пользовательское соглашение',
    shortTitle: 'Пользовательское соглашение',
    version: '1.0',
    effectiveOn: '2026-08-26',
    slug: 'terms',
    intro:
        'Настоящее соглашение регулирует использование сервиса Clothes — приложения для ' +
        'учёта личного гардероба и подбора образов. Регистрируясь, вы принимаете эти условия. ' +
        'Если вы с ними не согласны, не используйте сервис.',
    sections: [
        {
            id: 'terms-parties',
            title: '1. Стороны и термины',
            blocks: [
                {
                    kind: 'p',
                    text:
                        `Оператор сервиса — ${OPERATOR_NAME} (далее — «Оператор»). ` +
                        'Пользователь — физическое лицо, создавшее учётную запись в сервисе.',
                },
                {
                    kind: 'list',
                    items: [
                        '«Сервис» — веб-приложение Clothes, мобильное приложение и связанные с ними API.',
                        '«Учётная запись» — совокупность данных, идентифицирующих Пользователя в сервисе.',
                        '«Контент Пользователя» — фотографии вещей, названия, заметки, оценки, история носки и иные данные, которые Пользователь добавляет в сервис.',
                        '«AI-функции» — рекомендации образов, распознавание вещей по фотографии, анализ гардероба и покупок, выполняемые с использованием моделей машинного обучения.',
                    ],
                },
            ],
        },
        {
            id: 'terms-account',
            title: '2. Учётная запись',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Для использования сервиса требуется регистрация с указанием адреса электронной почты. ' +
                        'Адрес подтверждается по ссылке из письма: до подтверждения вход в сервис невозможен.',
                },
                {
                    kind: 'list',
                    items: [
                        'Пользователь отвечает за сохранность пароля и за все действия, совершённые под его учётной записью.',
                        'Пользователь обязуется указывать достоверный адрес электронной почты: на него отправляются уведомления о безопасности и восстановление доступа.',
                        'Одна учётная запись предназначена для одного человека. Передача доступа третьим лицам не предполагается; для совместного использования гардероба существует механизм прав доступа внутри сервиса.',
                        'Оператор вправе ограничить доступ к учётной записи при обоснованном подозрении на компрометацию, уведомив Пользователя по указанному адресу почты.',
                    ],
                },
            ],
        },
        {
            id: 'terms-usage',
            title: '3. Правила использования',
            blocks: [
                {kind: 'p', text: 'Пользователь обязуется не использовать сервис для:'},
                {
                    kind: 'list',
                    items: [
                        'размещения материалов, нарушающих законодательство Российской Федерации или права третьих лиц, в том числе авторские права на фотографии;',
                        'размещения изображений третьих лиц без их согласия;',
                        'автоматизированного сбора данных сервиса (парсинг, скрейпинг) вне предоставленных API и их документированных ограничений;',
                        'действий, создающих чрезмерную нагрузку на инфраструктуру сервиса или направленных на нарушение его работы;',
                        'обхода технических ограничений, включая лимиты AI-функций и правила доступа к чужим гардеробам.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Нарушение этих правил может повлечь ограничение доступа к отдельным функциям или ' +
                        'блокировку учётной записи. О блокировке Оператор уведомляет по адресу почты, ' +
                        'указанному при регистрации, с указанием причины.',
                },
            ],
        },
        {
            id: 'terms-content',
            title: '4. Контент Пользователя',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Контент Пользователя остаётся его собственностью. Оператор не претендует на права ' +
                        'на фотографии и иные материалы, загруженные в сервис.',
                },
                {
                    kind: 'p',
                    text:
                        'Пользователь предоставляет Оператору право хранить и обрабатывать этот контент ' +
                        'исключительно в объёме, необходимом для работы сервиса: отображения гардероба, ' +
                        'подбора образов, аналитики, резервного копирования и технической поддержки. ' +
                        'Это право прекращается вместе с удалением соответствующих данных.',
                },
                {
                    kind: 'p',
                    text:
                        'Контент Пользователя не используется для обучения моделей машинного обучения ' +
                        'и не передаётся третьим лицам для их обучения. Порядок передачи данных ' +
                        'AI-провайдерам при обработке запросов описан в Политике конфиденциальности.',
                },
            ],
        },
        {
            id: 'terms-ai',
            title: '5. AI-функции и характер рекомендаций',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Рекомендации образов, оценки вещей, выводы о структуре гардероба и советы по покупкам ' +
                        'носят рекомендательный характер. Они формируются автоматически и могут быть неточными.',
                },
                {
                    kind: 'list',
                    items: [
                        'Решения о покупке, продаже или утилизации вещей Пользователь принимает самостоятельно.',
                        'Оценки стоимости и прогнозы не являются офертой, оценочным заключением или финансовым советом.',
                        'Распознавание вещей по фотографии может ошибаться; результат доступен для ручной правки.',
                        'AI-функции доступны в пределах лимитов, зависящих от тарифа; лимиты и их текущее состояние отображаются в интерфейсе.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Пользователь вправе отключить AI-профилирование и обработку фотографий ' +
                        'в настройках приватности. Часть функций при этом станет недоступной — ' +
                        'это следствие отключения, а не ограничение доступа.',
                },
            ],
        },
        {
            id: 'terms-availability',
            title: '6. Доступность сервиса',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Сервис предоставляется «как есть». Оператор стремится обеспечить непрерывную работу, ' +
                        'но не гарантирует отсутствие перерывов, вызванных техническим обслуживанием, ' +
                        'сбоями оборудования, действиями провайдеров связи или обстоятельствами непреодолимой силы.',
                },
                {
                    kind: 'p',
                    text:
                        'О плановых работах, затрагивающих доступность сервиса более чем на час, ' +
                        'Оператор уведомляет заранее доступными в сервисе средствами.',
                },
            ],
        },
        {
            id: 'terms-liability',
            title: '7. Ответственность',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Оператор не несёт ответственности за убытки, возникшие вследствие решений, ' +
                        'принятых Пользователем на основании рекомендаций сервиса, а также за утрату данных, ' +
                        'вызванную действиями самого Пользователя (в том числе удалением вещей или учётной записи).',
                },
                {
                    kind: 'p',
                    text:
                        'Пользователь отвечает за правомерность размещаемого им контента и за соблюдение ' +
                        'прав третьих лиц при загрузке изображений.',
                },
                {
                    kind: 'p',
                    text:
                        'Ничто в настоящем соглашении не ограничивает права Пользователя как потребителя, ' +
                        'предусмотренные законодательством Российской Федерации.',
                },
            ],
        },
        {
            id: 'terms-subscription',
            title: '8. Тарифы и оплата',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Базовые функции сервиса предоставляются бесплатно. Отдельные функции и повышенные ' +
                        'лимиты могут предоставляться на платной основе.',
                },
                {
                    kind: 'list',
                    items: [
                        'Действующие тарифы и состав функций отображаются в интерфейсе сервиса до оформления подписки.',
                        'Изменение тарифов не затрагивает уже оплаченный период.',
                        'Порядок возврата средств определяется законодательством о защите прав потребителей.',
                    ],
                },
            ],
        },
        {
            id: 'terms-termination',
            title: '9. Прекращение использования',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Пользователь вправе в любой момент удалить учётную запись через настройки профиля ' +
                        'или по обращению на ' + CONTACT_EMAIL + '. Удаление учётной записи влечёт удаление ' +
                        'связанных с ней данных в сроки, указанные в Политике обработки персональных данных.',
                },
                {
                    kind: 'p',
                    text:
                        'Оператор вправе прекратить предоставление сервиса, уведомив Пользователей не менее ' +
                        'чем за 30 дней и обеспечив возможность выгрузки данных в машиночитаемом формате.',
                },
            ],
        },
        {
            id: 'terms-changes',
            title: '10. Изменение условий',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Оператор вправе изменять настоящее соглашение. Каждая редакция имеет номер версии ' +
                        'и дату вступления в силу; предыдущие редакции не удаляются.',
                },
                {
                    kind: 'p',
                    text:
                        'О существенных изменениях Пользователь уведомляется в сервисе или по адресу почты ' +
                        'не менее чем за 14 дней до вступления их в силу. Продолжение использования сервиса ' +
                        'после этой даты означает согласие с новой редакцией.',
                },
            ],
        },
        {
            id: 'terms-contacts',
            title: '11. Контакты и применимое право',
            blocks: [
                {
                    kind: 'p',
                    text:
                        `Оператор: ${OPERATOR_NAME}. Обращения принимаются по адресу ${CONTACT_EMAIL}.`,
                },
                {
                    kind: 'p',
                    text:
                        'К настоящему соглашению применяется право Российской Федерации. Споры разрешаются ' +
                        'путём переговоров, а при недостижении согласия — в порядке, установленном законодательством.',
                },
            ],
        },
    ],
};
